import { ChatMessageItem, ToolKind } from '../../fes';

export type ToolCall = {
  itemId: string;
  tool: ToolKind;
  preview?: string;
  output: string;
  status: 'running' | 'ok' | 'error';
};

export type ChatMessage = {
  id: string;
  role: 'user' | 'agent';
  content: string;
  reasoningFrames?: string[];
  tools?: ToolCall[];
  createdAt: string;
};

export function buildMessages(items: ChatMessageItem[]): ChatMessage[] {
  const result: ChatMessage[] = [];
  let agentContent: string[] = [];
  let agentReasoningFrames: string[] = [];
  let agentToolsMap = new Map<string, ToolCall>();
  let agentToolsOrder: string[] = [];
  let agentId = '';
  let agentCreatedAt = '';
  let sawTextDelta = false;

  const flushAgent = () => {
    const content = agentContent.join('').trimEnd();
    const tools = agentToolsOrder.map(toolId => agentToolsMap.get(toolId)).filter((tool): tool is ToolCall => Boolean(tool));

    if (content || agentReasoningFrames.length || tools.length) {
      result.push({
        id: agentId || `agent-${Date.now()}`,
        role: 'agent',
        content,
        reasoningFrames: agentReasoningFrames.length ? [...agentReasoningFrames] : undefined,
        tools: tools.length ? tools : undefined,
        createdAt: agentCreatedAt || new Date().toISOString(),
      });
    }

    agentContent = [];
    agentReasoningFrames = [];
    agentToolsMap = new Map();
    agentToolsOrder = [];
    agentId = '';
    agentCreatedAt = '';
    sawTextDelta = false;
  };

  items.forEach(item => {
    const payload = item.payload || {};
    const payloadType = typeof payload.type === 'string' ? payload.type : undefined;
    const itemId = typeof payload.itemId === 'string' ? payload.itemId : '';
    const delta = typeof payload.delta === 'string' ? payload.delta : '';
    const tool = typeof payload.tool === 'string' ? (payload.tool as ToolKind) : undefined;
    const preview = typeof payload.preview === 'string' ? payload.preview : undefined;
    const status = payload.status === 'error' ? 'error' : 'ok';
    const content = item.content || (typeof payload.content === 'string' ? payload.content : '');

    if (item.role === 'user') {
      flushAgent();

      if (content.trim()) {
        result.push({
          id: item.id,
          role: 'user',
          content,
          createdAt: item.createdAt,
        });
      }

      return;
    }

    if (!agentId) {
      agentId = item.id;
      agentCreatedAt = item.createdAt;
    }

    switch (payloadType) {
      case 'turn_complete':
      case 'done':
        flushAgent();
        break;
      case 'message_end':
      case 'telemetry':
      case 'status':
        break;
      case 'text_delta':
        if (delta) {
          agentContent.push(delta);
          sawTextDelta = true;
        }
        break;
      case 'reasoning_delta':
        if (delta.trim()) {
          agentReasoningFrames.push(delta.trim());
        }
        break;
      case 'tool_call_start':
        if (itemId && tool && !agentToolsMap.has(itemId)) {
          agentToolsMap.set(itemId, { itemId, tool, preview, output: '', status: 'running' });
          agentToolsOrder.push(itemId);
        }
        break;
      case 'tool_call_delta': {
        if (!itemId) {
          break;
        }

        const currentTool = agentToolsMap.get(itemId);

        if (currentTool) {
          currentTool.output += delta;
        } else if (tool) {
          agentToolsMap.set(itemId, { itemId, tool, output: delta, status: 'running' });
          agentToolsOrder.push(itemId);
        }
        break;
      }
      case 'tool_call_end': {
        if (!itemId) {
          break;
        }

        const currentTool = agentToolsMap.get(itemId);

        if (currentTool) {
          currentTool.status = status;
          if (preview) {
            currentTool.preview = preview;
          }
        } else if (tool) {
          agentToolsMap.set(itemId, { itemId, tool, preview, output: '', status });
          agentToolsOrder.push(itemId);
        }
        break;
      }
      case 'text':
      default:
        if (!sawTextDelta && content.trim()) {
          agentContent.push(content.endsWith('\n') ? content : `${content}\n`);
        }
        break;
    }
  });

  flushAgent();

  return result;
}
